import {
  Alert,
  AlertDescription,
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  chakra,
  Divider,
  Heading,
  keyframes,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import Header from '../../../../components/Header/Header';
import { useGetRegistration } from '../../../../services/hooks/Registration/useGetRegistration';
import CardHeaderMobile from '../../../../components/Mobile/header';
import LabelWithValueMobilePage from '../../../../components/Mobile/labelWithValue';
import TitleSectionMobilePage from '../../../../components/Mobile/titleSection';
import InvoicePDFButton from '../../../../components/PDFs/DownloadPDFButton/invoiceButton';
import { LiaFileInvoiceDollarSolid } from 'react-icons/lia';

const floatIn = keyframes`
      from { opacity: 0; transform: translateY(18px) scale(0.995); }  
  `;

const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

const PaymentReissueMobilePage: React.FC = () => {
  const border = useColorModeValue('green.300', 'green.600');
  const bg = useColorModeValue('green.50', 'green.900');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  const registrationId = useLocation().pathname.split('/')[3];

  const bgGradient = useColorModeValue('linear(to-br, gray.50, white)', 'linear(to-br, gray.900, gray.800)');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const getRegistration = useGetRegistration(registrationId || '');

  const candidate = getRegistration.data?.candidate;

  return (
    <Stack height="100%" w="100%" top={0} bg="white" gap={0}>
      <Header showGoBack />
      <VStack mt={0} flex={1} overflowY="auto" py={2}>
        {(getRegistration.isLoading && (
          <VStack flex={1} w="100%" justify="center">
            <Spinner emptyColor="gray.100" color="blue.500" size="xl" />
            <Text fontSize="sm">Buscando dados do boleto...</Text>
          </VStack>
        )) ||
          (getRegistration.isError && <Text>Erro ao trazer os dados.</Text>) || (
            <AnimatedBox
              bgGradient={bgGradient}
              borderRadius="lg"
              boxShadow="lg"
              mx={3}
              p={{ base: 5, md: 10 }}
              borderWidth={'1px'}
              borderColor={borderColor}
              bg="white"
              flex={1}
              display="flex"
            >
              <VStack boxSizing="border-box" maxW="lg" spacing={4} align="stretch">
                <CardHeaderMobile
                  title="Forma de pagamento"
                  subTitle="Caso ainda não tenha realizado o pagamento e precise solicitar, baixar ou reimprimir a segunda via, clique no botão Gerar Dados para Pagamento, selecione o meio de pagamento e emita a segunda via."
                  colorScheme="green"
                  badgeIcon={<LiaFileInvoiceDollarSolid size={13} />}
                  gap={1}
                  px={3}
                  badgeText="2º via"
                />
                <VStack spacing={2} align="stretch" h="100%">
                  <TitleSectionMobilePage>Dados da inscrição</TitleSectionMobilePage>
                  <LabelWithValueMobilePage label="Inscrição" value={getRegistration.data?.registration_code} />
                  <LabelWithValueMobilePage label="Situação" value={getRegistration.data?.registration_status.name} />
                  <LabelWithValueMobilePage
                    label="Processo seletivo"
                    value={getRegistration.data?.selective_process_vacancy?.selective_process?.name}
                  />
                  {getRegistration.data?.selective_process_vacancy?.name && (
                    <LabelWithValueMobilePage
                      label={
                        getRegistration.data?.selective_process_vacancy?.cultural_house
                          ? getRegistration.data?.selective_process_vacancy?.class_level
                            ? 'Turma '
                            : 'Turma - Primeira opção: '
                          : 'Vaga: '
                      }
                      value={getRegistration.data?.selective_process_vacancy.name}
                    />
                  )}
                  {getRegistration.data?.second_vacancy?.name && (
                    <>
                      <LabelWithValueMobilePage
                        label="Turma - Segunda opção"
                        value={getRegistration.data?.second_vacancy?.name}
                      />
                    </>
                  )}
                  <LabelWithValueMobilePage label="Nome completo" value={candidate?.user.full_name} />
                  <LabelWithValueMobilePage
                    label={candidate?.is_foreign ? 'Passaporte' : 'CPF'}
                    value={candidate?.is_foreign ? candidate.passport_number : candidate?.cpf}
                  />
                  {getRegistration.data?.invoice?.status_invoice && (
                    <LabelWithValueMobilePage
                      label="Situação do boleto"
                      value={getRegistration.data?.invoice?.status_invoice}
                    />
                  )}
                  {!getRegistration?.data?.registration_status?.name.toLowerCase().includes('aguardando') ? (
                    <></>
                  ) : (
                    <Stack gap={0} mt={3}>
                      <TitleSectionMobilePage>Pagamento</TitleSectionMobilePage>
                      {getRegistration.data?.candidate.is_foreign ? (
                        <Alert fontSize={'small'} status="info" flexDirection="column" gap={2}>
                          <Stack gap={1}>
                            <AlertDescription
                              lineHeight={1.5}
                              dangerouslySetInnerHTML={{
                                __html:
                                  getRegistration.data?.selective_process_vacancy?.selective_process
                                    ?.information_foreign_payment ||
                                  'No momento não foi adicionado nenhuma informação para pagamento.',
                              }}
                            />
                          </Stack>
                        </Alert>
                      ) : (
                        <Card border="1px solid" borderColor={border} borderRadius="lg" bg={bg} shadow="sm">
                          <CardHeader p={3}>
                            <Heading size="sm" color="green.600">
                              ⚠️&nbsp; Atenção
                            </Heading>
                          </CardHeader>

                          <Divider borderColor={border} />

                          <CardBody pb={1} color={textColor}>
                            <Text mb={3}>
                              Sua inscrição somente será efetivada após a confirmação do pagamento da taxa de inscrição.
                            </Text>

                            <Text fontWeight="medium">
                              <strong>Valor da inscrição:</strong> R${' '}
                              {getRegistration.data?.selective_process_vacancy?.value || '--'}
                            </Text>
                          </CardBody>

                          <CardFooter pt={0} mx="auto" w="100%">
                            <InvoicePDFButton registrationId={registrationId} />
                          </CardFooter>
                        </Card>
                      )}
                    </Stack>
                  )}
                </VStack>
              </VStack>
            </AnimatedBox>
          )}
      </VStack>
    </Stack>
  );
};

export default PaymentReissueMobilePage;
